/**
 * NotificationBell.jsx — floating bell with unread badge + dropdown panel.
 *
 * Mounts in the bottom-dock (next to the AI chat FAB) and stays present
 * across every authenticated route. Polls `/api/notifications/unread-count`
 * every 60s for the badge; expands a panel listing the user's
 * notifications on click. Each row deep-links to wherever the producing
 * feature wants (currently the only producer is `issue_reports_routes`).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Bell, CheckCheck, Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/App";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60_000;

function timeAgo(iso) {
  if (!iso) return "";
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

export default function NotificationBell() {
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  // Surface the unread count to the global bottom-dock so its collapse
  // toggle can show an "attention" dot while the dock is collapsed.
  useEffect(() => {
    try { window.dispatchEvent(new CustomEvent("notifications:unread", { detail: { unread } })); } catch { /* best-effort */ }
  }, [unread]);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);
  const btnRef = useRef(null);
  // Rect of the bell button so the panel opens anchored to the bell
  // wherever the dock sits (center, right, or dragged), instead of
  // being hard-pinned to the bottom-right corner of the screen.
  const [anchor, setAnchor] = useState(null);

  const captureAnchor = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setAnchor({ left: r.left, right: r.right, top: r.top, bottom: r.bottom, width: r.width });
  }, []);

  const fetchCount = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/api/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnread(r.data?.unread || 0);
    } catch {
      // silent — badge will simply not update
    }
  }, [token]);

  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/notifications?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(r.data?.items || []);
      setUnread(r.data?.unread || 0);
    } catch {
      toast.error("Couldn't load notifications");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Poll unread count on mount + every 60s.
  useEffect(() => {
    if (!token || !user) return undefined;
    fetchCount();
    pollRef.current = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [token, user, fetchCount]);

  // When opened, fetch the full list (so unread items render with proper styling).
  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const markOne = async (id) => {
    try {
      await axios.post(`${API}/api/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((rows) => rows.map((r) => (r.id === id ? { ...r, read: true } : r)));
      setUnread((n) => Math.max(0, n - 1));
    } catch {
      // ignore
    }
  };

  const markAll = async () => {
    try {
      await axios.post(`${API}/api/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((rows) => rows.map((r) => ({ ...r, read: true })));
      setUnread(0);
      toast.success("All notifications marked read");
    } catch {
      toast.error("Couldn't mark all read");
    }
  };

  const removeOne = async (id) => {
    try {
      await axios.delete(`${API}/api/notifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((rows) => rows.filter((r) => r.id !== id));
      fetchCount();
    } catch {
      toast.error("Couldn't delete");
    }
  };

  // Keep the anchor fresh while the panel is open (resize / scroll / dock move).
  useEffect(() => {
    if (!open) return undefined;
    captureAnchor();
    const onChange = () => captureAnchor();
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onChange, true);
    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, true);
    };
  }, [open, captureAnchor]);

  if (!user) return null;

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        data-testid="notification-bell"
        onClick={() => {
          if (open) { setOpen(false); return; }
          captureAnchor();
          setOpen(true);
        }}
        aria-label={unread > 0 ? `${unread} unread notifications` : "Notifications"}
        style={{
          position: "relative",
          width: 36, height: 36, borderRadius: 999,
          background: "transparent", border: 0,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", color: "var(--text-secondary, #475569)",
        }}
      >
        <Bell style={{ width: 17, height: 17 }} />
        {unread > 0 && (
          <span
            data-testid="notification-unread-badge"
            style={{
              position: "absolute", top: 4, right: 4,
              minWidth: 16, height: 16, padding: "0 4px",
              borderRadius: 999, background: "#dc2626", color: "white",
              fontSize: 9, fontWeight: 700,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 0 0 2px rgba(255,255,255,0.9)",
            }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <NotificationsPanel
          items={items}
          loading={loading}
          unread={unread}
          anchor={anchor}
          onClose={() => setOpen(false)}
          onMarkOne={markOne}
          onMarkAll={markAll}
          onRemove={removeOne}
          onRefresh={fetchList}
        />,
        document.body,
      )}
    </>
  );
}

function NotificationsPanel({ items, loading, unread, anchor, onClose, onMarkOne, onMarkAll, onRemove }) {
  // Anchor the panel to the bell button (auto-flip to stay on-screen).
  // Falls back to the old bottom-right corner if no rect was captured.
  const PW = 380, GAP = 10, MARGIN = 8;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  let posStyle;
  if (anchor) {
    // Horizontal: center the panel on the bell, clamped to the viewport.
    let left = anchor.left + anchor.width / 2 - PW / 2;
    left = Math.max(MARGIN, Math.min(left, vw - PW - MARGIN));
    const spaceAbove = anchor.top;
    const spaceBelow = vh - anchor.bottom;
    // Prefer opening ABOVE the bell when there's room; otherwise below.
    if (spaceAbove >= 260 || spaceAbove >= spaceBelow) {
      posStyle = { left, bottom: Math.max(MARGIN, vh - anchor.top + GAP), maxHeight: `${Math.min(vh * 0.7, spaceAbove - GAP - MARGIN)}px` };
    } else {
      posStyle = { left, top: anchor.bottom + GAP, maxHeight: `${Math.min(vh * 0.7, spaceBelow - GAP - MARGIN)}px` };
    }
  } else {
    posStyle = { right: 16, bottom: 72, maxHeight: "70vh" };
  }
  return (
    <>
      {/* click-outside catcher */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 9499,
          background: "transparent",
        }}
      />
      <div
        data-testid="notifications-panel"
        style={{
          position: "fixed",
          ...posStyle,
          width: PW,
          zIndex: 9500,
          background: "white",
          border: "0.5px solid rgba(0,0,0,0.12)",
          borderRadius: 14,
          boxShadow: "0 12px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)",
          display: "flex", flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div style={{
          padding: "12px 14px", borderBottom: "0.5px solid var(--border, #e5e7eb)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 650, color: "var(--text-primary, #0f172a)" }}>
              Notifications
            </div>
            <div style={{ fontSize: 11, color: "var(--text-tertiary, #94a3b8)", marginTop: 2 }}>
              {unread > 0 ? `${unread} unread` : "All caught up"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            {unread > 0 && (
              <button
                type="button"
                onClick={onMarkAll}
                data-testid="notifications-mark-all-read"
                title="Mark all read"
                style={{
                  background: "none", border: 0, padding: 6,
                  color: "var(--text-secondary, #475569)", cursor: "pointer",
                  borderRadius: 6,
                }}
              >
                <CheckCheck style={{ width: 14, height: 14 }} />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              data-testid="notifications-close"
              style={{
                background: "none", border: 0, padding: 6,
                color: "var(--text-secondary, #475569)", cursor: "pointer",
                borderRadius: 6,
              }}
            >
              <X style={{ width: 14, height: 14 }} />
            </button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 6 }}>
          {loading && items.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "var(--text-tertiary, #94a3b8)" }}>
              <Loader2 className="animate-spin" style={{ width: 16, height: 16, display: "inline-block" }} />
            </div>
          ) : items.length === 0 ? (
            <div style={{
              padding: "32px 16px", textAlign: "center", color: "var(--text-tertiary, #94a3b8)",
              fontSize: 12,
            }}>
              No notifications yet. When something needs your attention, it'll show up here.
            </div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 4 }}>
              {items.map((n) => (
                <NotificationRow
                  key={n.id}
                  notif={n}
                  onMark={() => onMarkOne(n.id)}
                  onRemove={() => onRemove(n.id)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

function NotificationRow({ notif, onMark, onRemove }) {
  const handleClick = () => {
    if (!notif.read) onMark();
    // Phase 1.7 — deep-link navigation when the producing feature
    // supplied a `link` (e.g. closed_won notifications point to the
    // new contact card). Skip external URLs to be defensive — only
    // navigate to in-app paths.
    const link = notif.link || "";
    if (link && link.startsWith("/")) {
      // Use assign() so the bell panel's portal unmounts cleanly via
      // route change, and so the browser back-button works as expected.
      window.location.assign(link);
    }
  };
  return (
    <li
      data-testid={`notification-row-${notif.id}`}
      onClick={handleClick}
      style={{
        padding: "10px 12px",
        borderRadius: 8,
        cursor: notif.read ? "default" : "pointer",
        background: notif.read ? "transparent" : "rgba(37, 99, 235, 0.06)",
        border: notif.read ? "0.5px solid transparent" : "0.5px solid rgba(37, 99, 235, 0.18)",
        display: "flex", gap: 10, alignItems: "flex-start",
        transition: "background 120ms ease",
      }}
    >
      <div style={{
        width: 6, height: 6, borderRadius: 999,
        background: notif.read ? "transparent" : "#2563eb",
        marginTop: 7, flexShrink: 0,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 12.5, fontWeight: notif.read ? 500 : 650,
          color: "var(--text-primary, #0f172a)",
          marginBottom: 2,
        }}>
          {notif.title}
        </div>
        {notif.body && (
          <div style={{
            fontSize: 11.5, color: "var(--text-secondary, #475569)",
            whiteSpace: "pre-wrap", overflowWrap: "anywhere",
            lineHeight: 1.45,
          }}>
            {notif.body}
          </div>
        )}
        <div style={{
          fontSize: 10, color: "var(--text-tertiary, #94a3b8)",
          marginTop: 4, fontVariantNumeric: "tabular-nums",
        }}>
          {timeAgo(notif.created_at)}
        </div>
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        data-testid={`notification-delete-${notif.id}`}
        aria-label="Dismiss notification"
        style={{
          background: "none", border: 0, padding: 4,
          color: "var(--text-tertiary, #94a3b8)", cursor: "pointer",
          flexShrink: 0,
        }}
      >
        <Trash2 style={{ width: 12, height: 12 }} />
      </button>
    </li>
  );
}
